import { Button, Card, Input, Modal, Table } from "antd";
import "bootstrap-icons/font/bootstrap-icons.css";
import dayjs from "dayjs";
import { useEffect, useState, useMemo } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserDevices,
  removeBiometric,
} from "../../redux/rtk/features/user/userSlice";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { CsvLinkBtn } from "../UI/CsvLinkBtn";

// Date functionalities
let startdate = dayjs().startOf("month");
let enddate = dayjs().endOf("month");

function CustomTable({ list, total, searchText, filteredData, columnsToShow, pagination, setPagination }) {
  const dispatch = useDispatch();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: total || 0 }));
  }, [total, setPagination]);

  const columnsWithAction = useMemo(() => {
    return columnsToShow.map(col => {
      if (col.id === 3) {
        return {
          ...col,
      render: (text, record) => (
        <Button
          danger
          onClick={() => {
            setSelectedRecord(record);
            setIsModalVisible(true);
          }}
        >
          Remove
        </Button>
      ),
        };
      }
      if (col.id === 0) {
        return {
          ...col,
          render: (text, record, index) => {
            return (pagination.current - 1) * pagination.pageSize + index + 1;
          },
        };
      }
      return col;
    });
  }, [columnsToShow, pagination.current, pagination.pageSize]);

  const handleOk = () => {
    if (selectedRecord) {
      dispatch(removeBiometric(selectedRecord.EmployeeId))
        .then(() => {
          dispatch(getUserDevices({ page: pagination.current, pageSize: pagination.pageSize }));
          setIsModalVisible(false);
        })
        .catch((error) => {
          // console.error("Failed to remove biometric:", error);
          setIsModalVisible(false);
        });
    } else {
      setIsModalVisible(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const addKeys = (arr) => arr.map((i) => ({ ...i, key: i.userDeviceId }));

  return (
    <>
      <Table
        scroll={{ x: true, y: "100%" }}
        loading={!list}
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: [10, 20, 50, 100, 200],
          showSizeChanger: true,
          total: pagination.total,
          current: pagination.current,
          pageSize: pagination.pageSize,
          onChange: (page, pageSize) => {
            setPagination({ ...pagination, current: page, pageSize });
            dispatch(getUserDevices({ page, pageSize }));
          },
        }}
        columns={columnsWithAction}
        dataSource={filteredData ? addKeys(filteredData) : []}
      />
      <Modal
        title="Confirm Removal"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>
          Are you sure you want to remove the biometric data for{" "}
          <strong>
            {selectedRecord?.FirstName || ''} {selectedRecord?.LastName || ''}
          </strong>
          {selectedRecord?.EmployeeCode && (
            <span> (Employee Code: {selectedRecord.EmployeeCode})</span>
          )}
          ?
        </p>
      </Modal>
    </>
  );
}

const RemoveBiometric = () => {
  const dispatch = useDispatch();
  const userDeviceDetails = useSelector(
    (state) => state?.users?.userDeviceDetails
  ) || {};
  const [searchText, setSearchText] = useState("");
  const [columnsToShow, setColumnsToShow] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    dispatch(getUserDevices({ page: 1, pageSize: 10 }));
  }, [dispatch]);

  useEffect(() => {
    const userList = Array.isArray(userDeviceDetails) ? userDeviceDetails : [];
    if (searchText) {
      const filtered = userList.filter((item) => {
        const fullName = `${item.FirstName || ''} ${item.LastName || ''}`.toLowerCase();
        const employeeCode = (item.EmployeeCode || '').toLowerCase();
        return fullName.includes(searchText.toLowerCase()) || 
               employeeCode.includes(searchText.toLowerCase());
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(userList);
    }
  }, [searchText, userDeviceDetails]);

  const columns = useMemo(() => [
    {
      id: 0,
      title: "S.No",
      key: "serial_number",
    },
    {
      id: 1,
      title: "Name",
      key: "name",
      render: (text, record) => {
        const firstName = record.FirstName || '';
        const lastName = record.LastName || '';
        return <span>{`${firstName} ${lastName}`.trim() || '-'}</span>;
      },
    },
    {
      id: 2,
      title: "Employee Code",
      key: "employee_code",
      render: (text, record) => (
        <span>{record.EmployeeCode || '-'}</span>
      ),
    },
    {
      id: 3,
      title: "Remove Biometric",
      key: "remove_biometric",
    },
  ], []);

  useEffect(() => {
    setColumnsToShow(columns);
  }, [columns]);

  const CSVlist = Array.isArray(filteredData)
    ? filteredData.map((i) => ({
        ...i,
        biometric: i?.biometric?.name,
        credit: i?.credit?.name,
      }))
    : [];

  return (
    <div className="remove-biometric-page-container">
        <Card className="remove-biometric-card">
        <div className="card-title flex justify-between items-center mr-4 remove-biometric-header">
          <h5 className="text-xl txt-color-2">
                <span>Remove Biometric</span>
              </h5>
          <div className="flex items-center gap-3">
                <Input
                  placeholder="Search by name or employee code"
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                  size="small"
                />
                <CsvLinkBtn>
              <CSVLink data={CSVlist} filename="user.csv">
                    Download CSV
                  </CSVLink>
                </CsvLinkBtn>
                {userDeviceDetails && (
                  <ColVisibilityDropdown
                    options={columns}
                    columns={columns}
                columnsToShowHandler={(val) => setColumnsToShow(val)}
                  />
                )}
            </div>
          </div>
          <CustomTable
          list={userDeviceDetails}
            startdate={startdate}
            enddate={enddate}
          total={userDeviceDetails?.total || 0}
            searchText={searchText}
          filteredData={filteredData}
            columnsToShow={columnsToShow}
          pagination={pagination}
          setPagination={setPagination}
          />
        </Card>
      </div>
  );
};

export default RemoveBiometric;

